/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { App, type PluginListenerHandle } from "@capacitor/app";
import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/contexts/AuthContext";
import { usePendingInvite } from "@/contexts/PendingInviteContext";
import {
  PENDING_INVITE_LS_KEY,
  parseInviteToken,
} from "@/utils/invite/inviteLogic";
import { parseTrackSessionId } from "@/utils/walkMeHome/trackLink";

export const INVITE_EVENT = "myambits:invite";

/**
 * Dispatch a legacy event. Kept as an escape hatch for non-React call sites
 * but prefer `usePendingInvite().setPendingInviteToken(token)` from inside a
 * component.
 */
export function dispatchInviteEvent(token: string) {
  window.dispatchEvent(new CustomEvent(INVITE_EVENT, { detail: { token } }));
}

/**
 * Registers Capacitor deep-link listeners at the app root and routes any
 * discovered invite tokens into the PendingInviteContext.
 *
 * Auth race handling: when the URL arrives before auth resolves (cold launch
 * from iMessage) the token is stashed in an in-memory ref. As soon as auth
 * resolves with a signed-in user, we flush the ref into context. No
 * localStorage — it's stale across cold launches with different accounts.
 *
 * Must be mounted exactly once, high in the tree (App.tsx), not per-route,
 * so the listener is live even when the user is signed out.
 */
export function useDeepLinks() {
  const { user, authResolved } = useAuth();
  const { setPendingInviteToken } = usePendingInvite();
  const pendingTokenRef = useRef<string | null>(null);

  // Stable refs so the effect below doesn't re-subscribe on every user change.
  const userRef = useRef(user);
  userRef.current = user;
  const authResolvedRef = useRef(authResolved);
  authResolvedRef.current = authResolved;
  const setPendingInviteTokenRef = useRef(setPendingInviteToken);
  setPendingInviteTokenRef.current = setPendingInviteToken;

  useEffect(() => {
    const ingest = (url: string | null | undefined, source: string) => {
      // Walk Me Home tracking link (SMS to an emergency contact). If the app is
      // installed, a tapped /track/{sessionId} universal link opens here — route
      // it to the in-app public tracker instead of letting it fall through to
      // the homepage/welcome screen (the reported bug). No auth gate: the
      // tracker is a public route and reads the session by its unguessable id.
      // Handled independently of, and before, invite-token parsing.
      const trackSessionId = parseTrackSessionId(url);
      if (trackSessionId) {
        Sentry.addBreadcrumb({
          category: "deeplink",
          message: "track_link_opened",
          level: "info",
          data: { source },
        });
        const target = `/track/${trackSessionId}`;
        if (window.location.pathname !== target) {
          window.location.assign(target);
        }
        return;
      }

      const token = parseInviteToken(url);
      if (!token) return;
      Sentry.addBreadcrumb({
        category: "deeplink",
        message: "invite_token_parsed",
        level: "info",
        data: { source },
      });
      if (!authResolvedRef.current || !userRef.current) {
        // Hold until auth is resolved and a user is present.
        pendingTokenRef.current = token;
        Sentry.addBreadcrumb({
          category: "deeplink",
          message: "invite_token_queued",
          level: "info",
          data: { source, reason: !authResolvedRef.current ? "auth_unresolved" : "no_user" },
        });
        return;
      }
      setPendingInviteTokenRef.current(token);
      Sentry.addBreadcrumb({
        category: "deeplink",
        message: "invite_token_dispatched",
        level: "info",
        data: { source },
      });
    };

    // Cold-launch URL (app was not running when the link was tapped).
    App.getLaunchUrl()
      .then((r) => ingest(r?.url, "launch"))
      .catch((err) => {
        console.warn("[useDeepLinks] getLaunchUrl failed:", err);
      });

    let handle$: Promise<PluginListenerHandle> | null = null;
    try {
      handle$ = App.addListener("appUrlOpen", ({ url }) => ingest(url, "appUrlOpen"));
    } catch (err) {
      console.warn("[useDeepLinks] addListener failed:", err);
    }

    const onLegacyEvent = (e: Event) => {
      const token = (e as CustomEvent<{ token?: string }>).detail?.token;
      if (token) ingest(`/invite/${token}`, "legacy_event");
    };
    window.addEventListener(INVITE_EVENT, onLegacyEvent);

    return () => {
      handle$?.then((h) => h.remove()).catch(() => {});
      window.removeEventListener(INVITE_EVENT, onLegacyEvent);
    };
    // Mount once. Replay logic runs in the separate effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When auth transitions to "resolved + user present", replay any queued
  // token. Clearing the ref first avoids a second replay if this effect
  // re-runs for any reason.
  //
  // Also drains the web "Accept in Browser" handoff (localStorage), which
  // InviteLanding sets before bouncing the visitor through AuthScreen. The
  // in-memory ref takes precedence so this never double-fires when both
  // channels happen to be primed.
  useEffect(() => {
    if (!authResolved || !user) return;
    const queued = pendingTokenRef.current;
    if (queued) {
      pendingTokenRef.current = null;
      Sentry.addBreadcrumb({
        category: "deeplink",
        message: "invite_token_replayed",
        level: "info",
      });
      setPendingInviteToken(queued);
      return;
    }
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(PENDING_INVITE_LS_KEY);
    } catch {
      stored = null;
    }
    if (stored) {
      try {
        localStorage.removeItem(PENDING_INVITE_LS_KEY);
      } catch {
        // ignore
      }
      Sentry.addBreadcrumb({
        category: "deeplink",
        message: "invite_token_replayed",
        level: "info",
        data: { source: "localStorage" },
      });
      setPendingInviteToken(stored);
    }
  }, [authResolved, user, setPendingInviteToken]);
}
